import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Box } from "@chakra-ui/react";

import axios from "axios";
import { neutral } from "../../../theme/tokens";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import { isDiscontinued } from "../../../tools/discontinued";

// The matrix is transposed: a row is a store, a column is an article. The
// first three columns (site code, site description, region) are fixed; every
// column after them is an article, so that is where the discontinued filter
// starts looking.
const FIXED_COLS = 3;

// Height of the horizontal scrollbar strip that sits above the grid. It lives
// outside the scroll box, so the box can only be as tall as what is left.
const TOP_SCROLLBAR_H = 18;

// First-paint height, replaced by a measurement as soon as the grid is on the
// page. A constant cannot get this right on its own: the toolbar wraps on
// narrow viewports and the shell puts a Footer below the report, so a guess
// that runs long leaves the WINDOW scrolling as well -- two vertical
// scrollbars, and a wheel that bottoms out in the grid then drags the whole
// panel, frozen header included, up the page.
const TABLE_MAX_H_INITIAL = `calc(100vh - ${240 + TOP_SCROLLBAR_H}px)`;
const TABLE_MIN_H = 240;

const Table1 = ({ supplier, retailer, hideDiscontinued = false, onDiscontinuedCount }) => {
  const [thead, setThead] = useState({ th0: [], th1: [] });
  const [tbody, setTbody] = useState([]);
  const [loading, setLoading] = useState(true);
  const syncingRef = useRef(false);
  const headRow0Ref = useRef(null);
  const containerRef = useRef(null);
  const [maxH, setMaxH] = useState(TABLE_MAX_H_INITIAL);
  // The second header row parks directly under the first, so it needs the
  // first row's measured height rather than a hardcoded guess that browser
  // zoom or a taller font would knock out of alignment.
  const [headRow0H, setHeadRow0H] = useState(29);

  // Column indexes carrying a discontinued article, held as a Set of source
  // indexes so the header rows and the body all filter against one answer.
  //
  // The verdict comes from the server (head.colFlags, one flag per column),
  // because the marker is usually the article's BRAND -- 'DISC', 'PNP DISC',
  // 'OAKMONT DISC' -- and brand never reaches this payload. Only a handful of
  // Dis-Chem lines carry it in the description, which is all the fallback below
  // can see: it keeps the toggle partly working against a backend that predates
  // the flag rather than leaving it silently dead.
  //
  // (`colFlags`, not `disc`: the demo route string-replaces brand names over the
  // whole serialised payload, key names included, and `DISC` is a real brand.)
  const discontinuedCols = useMemo(() => {
    const cols = new Set();
    if (Array.isArray(thead.colFlags)) {
      thead.colFlags.forEach((flag, index) => {
        if (index >= FIXED_COLS && flag) cols.add(index);
      });
      return cols;
    }
    (thead.th1 || []).forEach((articleName, index) => {
      if (index >= FIXED_COLS && isDiscontinued(articleName)) cols.add(index);
    });
    return cols;
  }, [thead]);

  // Kept as a list of source indexes rather than hiding cells with CSS, so the
  // table actually narrows and the horizontal scroll shortens when the filter
  // is on.
  const visibleCols = useMemo(() => {
    const total = Math.max((thead.th0 || []).length, (thead.th1 || []).length);
    const cols = [];
    for (let i = 0; i < total; i++) {
      if (hideDiscontinued && discontinuedCols.has(i)) continue;
      cols.push(i);
    }
    return cols;
  }, [thead, hideDiscontinued, discontinuedCols]);

  useEffect(() => {
    if (typeof onDiscontinuedCount === "function") onDiscontinuedCount(discontinuedCols.size);
  }, [discontinuedCols, onDiscontinuedCount]);

  useEffect(() => {
    const tableContainer = document.getElementById("table-container");
    const stickyScrollbar = document.getElementById("sticky-scrollbar");
    const scrollbarContent = document.getElementById("scrollbar-content");

    if (!tableContainer || !stickyScrollbar || !scrollbarContent) return;

    // Set the width of the scrollbar content to match table width
    const updateScrollbarWidth = () => {
      const table = tableContainer.querySelector("table");
      if (table) {
        const tableWidth = table.scrollWidth;
        const containerWidth = tableContainer.clientWidth;
        // The grid reserves a gutter for its vertical scrollbar; the strip,
        // being overflow-y hidden, does not -- so the strip's viewport is wider
        // by exactly that much. Widen its spacer to match, or the two boxes
        // clamp scrollLeft at different maxima: the strip can no longer reach
        // the last column, and the grid snaps back by the gutter width every
        // time the user scrolls it to its right edge (the sync writes the
        // clamped value straight back).
        const gutter = Math.max(0, stickyScrollbar.clientWidth - containerWidth);
        scrollbarContent.style.width = tableWidth + gutter + "px";

        // Show/hide scrollbar based on whether scrolling is needed
        if (tableWidth > containerWidth) {
          stickyScrollbar.style.display = "block";
        } else {
          stickyScrollbar.style.display = "none";
        }
      }
    };

    // Improved sync functions with debouncing
    const syncTableScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
        syncingRef.current = false;
      });
    };

    const syncStickyScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        tableContainer.scrollLeft = stickyScrollbar.scrollLeft;
        syncingRef.current = false;
      });
    };

    // Initial setup with proper timing
    const initializeScrollbar = () => {
      updateScrollbarWidth();
      // Sync initial scroll positions
      stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
    };

    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(initializeScrollbar, 150);

    // Add event listeners with passive option for better performance
    tableContainer.addEventListener("scroll", syncTableScroll, { passive: true });
    stickyScrollbar.addEventListener("scroll", syncStickyScroll, { passive: true });

    // Update scrollbar width when window resizes
    const handleResize = () => {
      setTimeout(updateScrollbarWidth, 100);
    };
    window.addEventListener("resize", handleResize);

    // Observer to update scrollbar when table content changes
    const observer = new MutationObserver(() => {
      setTimeout(updateScrollbarWidth, 50);
    });
    observer.observe(tableContainer, { childList: true, subtree: true, attributes: true });

    return () => {
      tableContainer.removeEventListener("scroll", syncTableScroll);
      stickyScrollbar.removeEventListener("scroll", syncStickyScroll);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [tbody, thead, visibleCols]);

  useEffect(() => {
    const el = document.getElementById("table-container");
    if (!el) return;

    let down = false;
    let startX = 0;
    let scrollStart = 0;

    const downHandler = (e) => {
      if (e.button !== 0) return;
      down = true;
      startX = e.clientX;
      scrollStart = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const moveHandler = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      el.scrollLeft = scrollStart - dx;
    };

    const upHandler = () => {
      if (!down) return;
      down = false;
      el.style.cursor = "grab";
      el.style.removeProperty("user-select");
    };

    el.addEventListener("mousedown", downHandler);
    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", upHandler);

    return () => {
      el.removeEventListener("mousedown", downHandler);
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", upHandler);
    };
  }, [loading]);

  // Size the grid to the space actually left between it and the bottom of the
  // viewport, so the window itself never needs to scroll. `belowGrid` is the
  // footer and page padding underneath: it does not change when the grid
  // resizes (document height and grid height move together), so this settles
  // in one pass instead of oscillating.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const absTop = rect.top + window.scrollY;
      const belowGrid = document.documentElement.scrollHeight - (absTop + rect.height);
      const available = Math.max(TABLE_MIN_H, Math.round(window.innerHeight - absTop - belowGrid));
      setMaxH((prev) => (prev === `${available}px` ? prev : `${available}px`));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading, thead, visibleCols]);

  // Measure the top header row so the second one can pin right beneath it.
  useLayoutEffect(() => {
    const el = headRow0Ref.current;
    if (!el) return;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) setHeadRow0H(h);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, thead, visibleCols]);

  useEffect(() => {
    const buildTable = async () => {
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_ranging/html-rangingReport`, {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        });
        const { status, data } = res.data;
        if (status === "success") {
          setThead(data.head);
          setTbody(data.body);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setLoading(false);
      }
    };

    buildTable();
  }, [supplier, retailer]);

  if (loading) return <TableSkeleton rows={12} minH="420px" />;

  // Widths are fixed per column position and the first two columns are frozen
  // to the left, so both header rows and every body row read the same rules.
  const colWidth = (index) => (index === 0 ? "80px" : index === 1 ? "180px" : index === 2 ? "120px" : "70px");
  const colLeft = (index) => (index === 0 ? 0 : index === 1 ? "80px" : "auto");

  return (
    <Box position="relative">
      {/* Table-specific Sticky Horizontal Scrollbar at Top */}
      <Box
        id="sticky-scrollbar"
        position="sticky"
        top={0}
        left={0}
        right={0}
        height={`${TOP_SCROLLBAR_H}px`}
        bg="white"
        borderBottom="2px solid"
        borderColor={neutral[200]}
        zIndex={25}
        display="none"
        overflowX="auto"
        overflowY="hidden"
        css={{
          scrollBehavior: "auto",
          "&::-webkit-scrollbar": {
            height: "14px",
          },
          "&::-webkit-scrollbar-track": {
            background: neutral[100],
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: neutral[300],
            borderRadius: "4px",
            border: `2px solid ${neutral[0]}`,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: neutral[400],
          },
          "&::-webkit-scrollbar-thumb:active": {
            background: neutral[500],
          },
        }}
      >
        <Box id="scrollbar-content" height="1px" minWidth="100%" />
      </Box>

      {/* This box is the scroll container for both axes. A sticky header can
          only pin to its own scroll container, and a box with overflow-x is
          one whether or not it scrolls vertically — so while the page scrolled
          instead, the header had nothing to hold on to and left with it.
          Capping the height here is what freezes it. The native horizontal bar
          is suppressed because the strip above already drives that axis. */}
      <Box
        id="table-container"
        ref={containerRef}
        overflowX="auto"
        overflowY="auto"
        maxHeight={maxH}
        sx={{ cursor: "grab", scrollBehavior: "auto", overscrollBehavior: "contain" }}
        css={{
          "&::-webkit-scrollbar": {
            width: "12px",
            height: "12px",
          },
          // Only the horizontal bar goes: the strip above the grid drives that
          // axis. Targeted with `:horizontal` rather than `height: 0` on the
          // shared rule, which would be relying on `height` never reaching the
          // vertical bar - and vertical is the one this change exists to add.
          "&::-webkit-scrollbar:horizontal": {
            height: "0px",
          },
          "&::-webkit-scrollbar-track": {
            background: neutral[100],
          },
          "&::-webkit-scrollbar-thumb": {
            background: neutral[300],
            borderRadius: "6px",
            border: `3px solid ${neutral[0]}`,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: neutral[400],
          },
        }}
      >
        <Table
          size="sm"
          variant="unstyled"
          width="max-content"
          sx={{
            minWidth: "max-content",
            tableLayout: "fixed",
            "th:nth-of-type(n+4), td:nth-of-type(n+4)": {
              maxWidth: "50px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        >
          <Thead>
            <Tr ref={headRow0Ref}>
              {visibleCols.map((colIndex) => (
                <Th key={`th0-${colIndex}`} width={colWidth(colIndex)} borderRight="1px solid" borderColor={neutral[200]} position="sticky" top={0} left={colLeft(colIndex)} zIndex={colIndex < 2 ? 32 : 30} bg={neutral[100]} color={neutral[700]} px={2} py={2} fontSize="10px" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" shadow={colIndex < 2 ? "md" : "none"} lineHeight="1.2">
                  <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {thead.th0[colIndex]}
                  </Box>
                </Th>
              ))}
            </Tr>
            <Tr>
              {visibleCols.map((colIndex) => (
                <Th key={`th1-${colIndex}`} width={colWidth(colIndex)} borderRight="1px solid" borderColor={neutral[200]} position="sticky" top={`${headRow0H}px`} left={colLeft(colIndex)} zIndex={colIndex < 2 ? 31 : 29} bg={neutral[50]} color={neutral[500]} px={2} py={1.5} fontSize="9px" fontWeight="medium" shadow={colIndex < 2 ? "md" : "none"} lineHeight="1.1">
                  <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={thead.th1[colIndex]}>
                    {thead.th1[colIndex]}
                  </Box>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {tbody.map((row, rowIndex) => (
              <Tr key={`row-${rowIndex}`} bg={rowIndex % 2 === 0 ? neutral[25] : neutral[0]} _hover={{ bg: neutral[100] }} borderBottom="1px solid" borderColor={neutral[200]}>
                {visibleCols.map((cellIndex) => {
                  const cell = row[cellIndex];
                  if (!cell) return null;
                  return (
                    <Td
                      key={`cell-${rowIndex}-${cellIndex}`}
                      sx={{
                        backgroundColor: cellIndex < 2 ? (rowIndex % 2 === 0 ? neutral[25] : neutral[0]) : `${cell.color} !important`,
                        whiteSpace: "nowrap",
                        minWidth: colWidth(cellIndex),
                      }}
                      width={colWidth(cellIndex)}
                      borderRight="1px solid"
                      borderColor="gray.200"
                      position={cellIndex < 2 ? "sticky" : "static"}
                      left={colLeft(cellIndex)}
                      zIndex={cellIndex < 2 ? 15 : 1}
                      px={2}
                      py={1}
                      fontSize="11px"
                      fontWeight={cellIndex < 2 ? "medium" : "normal"}
                      shadow={cellIndex < 2 ? "sm" : "none"}
                      lineHeight="1.3"
                    >
                      <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={cell.value} maxW="100%">
                        {cell.value}
                      </Box>
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Table1;
